import { mapMaterialsToWriteMaterials } from '#api/adapters/materialAdapter.ts';
import type { ApiWriteOrder as ApiWriteOrderV2, ApiWriteStop as ApiWriteStopV2 } from '#api/model/types.ts';
import type { Material } from '#domain/Material.ts';
import { isCargoPackagingType } from '#domain/Packaging.ts';
import { parseGeoCoordinateString } from '#utils/geoParsing.util.ts';

import type {
    Location as FormDataLocation,
    MaterialFormData,
    OrderFormData,
    StopFormData,
} from './OrderFormValidation.ts';
import { composeDateTimeValue } from './timeWindowFields.ts';

export type OrderWriteFormData = Pick<
    OrderFormData,
    'number' | 'customerName' | 'loadingComment' | 'unloadingComment' | 'loadingStop' | 'unloadingStop' | 'materials'
>;

const mapWriteLocationV2 = (stopLocation: FormDataLocation): ApiWriteStopV2['location'] => ({
    street: stopLocation.street.trim(),
    city: stopLocation.city,
    zip_code: stopLocation.zipCode,
    country: stopLocation.country,
});

const mapWriteStopV2 = (stop: StopFormData): ApiWriteStopV2 => ({
    name: stop.name,
    location: mapWriteLocationV2(stop.location),
    start_target: composeDateTimeValue(stop.startDate, stop.startTime),
    end_target: composeDateTimeValue(stop.endDate, stop.endTime),
    type: stop.type === 'load' || stop.type === 'unload' ? stop.type : 'load',
    coordinates: mapCoordinates(stop),
});

const mapCoordinates = (stop: StopFormData) => {
    if (stop.latitude !== undefined && stop.longitude !== undefined) {
        return {
            latitude: parseGeoCoordinateString(stop.latitude),
            longitude: parseGeoCoordinateString(stop.longitude),
        };
    }

    if (stop.location.latitude !== undefined && stop.location.longitude !== undefined) {
        return {
            latitude: parseGeoCoordinateString(stop.location.latitude),
            longitude: parseGeoCoordinateString(stop.location.longitude),
        };
    }
    return undefined;
};

const mapFormMaterialToDomain = (material: MaterialFormData, index: number): Material => {
    if (material.type === 'cargo-material') {
        const packageType = isCargoPackagingType(material.packageType) ? material.packageType : 'unspecified';
        return {
            id: material.id ?? '',
            type: 'cargo-material',
            name: material.name,
            packageType,
            index: material.index ?? index,
            quantity: { target: material.quantity },
            weight: material.weight !== undefined ? { target: material.weight } : undefined,
            loadingMeter: material.loadingMeter !== undefined ? { target: material.loadingMeter } : undefined,
            length: material.length,
            width: material.width,
            height: material.height,
        };
    }

    return {
        id: material.id ?? '',
        type: 'bulk-material',
        packageType: 'bulk-material',
        name: material.name,
        unitType: material.unitType,
        quantity: { target: material.quantity },
        index: material.index ?? index,
    };
};

export const mapWriteOrder = (order: OrderWriteFormData): ApiWriteOrderV2 => ({
    type: 'transport',
    number: order.number,
    customer_name: order.customerName,
    comments:
        order.loadingComment || order.unloadingComment
            ? {
                  loading: order.loadingComment,
                  unloading: order.unloadingComment,
              }
            : undefined,
    loading_stop: mapWriteStopV2(order.loadingStop),
    unloading_stop: mapWriteStopV2(order.unloadingStop),
    materials: mapMaterialsToWriteMaterials(order.materials.map(mapFormMaterialToDomain)),
});
